import type { LineItem } from "@graphql/generated/types";
import { CommonCMS } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Attributes } from "@lib/enums/ProductAttributes";
import { getEnrichedPageTitle, getImageAltTag } from "@lib/utils/attributesUtils";
import {
    getImageData,
    getLineItemAttribute,
    getPriceBefore,
    getPriceNow,
} from "@lib/utils/cartProductUtility";
import { getProductSizeLabel, hasOneSize } from "@lib/utils/productUtility";
import { useStore } from "@store/index";
import {
    ProductRow as ProductRowContainer,
    ProductRowHeadline,
    ProductRowImage,
    ProductRowPrice,
    ProductRowMeta,
} from "@ecco/ui/product-row";
import { Text } from "@ecco/ui/text";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { styles } from "./ProductRowStyles";
import { useGetAllOffers } from "../editable-row/hooks/useGetAllOffers";
import { GIFT_WRAPPING_KEY } from "@lib/constants/product";
import { useMemo } from "react";
import { isEmpty } from "lodash-es";

interface ProductRowProps {
    item?: LineItem;
    locale: string;
    isOrderTracking?: boolean;
    isDetailed?: boolean;
    isReturn?: boolean;
}

const ProductRow = ({
    item,
    locale,
    isOrderTracking = false,
    isDetailed = true,
    isReturn = false,
}: ProductRowProps) => {
    const { getMultiple } = useMicrocopy();
    const currentShoeConvention = useStore((state) => state.data.me.globalShoeSizeConvention);
    const offers = useGetAllOffers(item);

    const microcopies = useMemo(() => {
        const page = getMultiple(CommonCMS.page, [
            ClickAndCollect.available,
            ClickAndCollect.availableSoon,
        ]);
        const shared = getMultiple(CommonCMS.shared, [CommonCMS.size.title, CommonCMS.color]);
        return { ...page, ...shared };
    }, []);

    if (!item) return null;

    const image = getImageData(item, Attributes.outsideImage) ?? getImageData(item);
    const conventionLabel =
        microcopies[`uiText.sizeLabel${currentShoeConvention}`] || currentShoeConvention;
    const { label: productSizeLabel, rawLabel: rawProductSizeLabel } = getProductSizeLabel({
        variant: item.variant ?? undefined,
        shoeConvention: currentShoeConvention,
        affix: conventionLabel,
    });
    const altTagPattern = getEnrichedPageTitle(
        item.variant?.attributesRaw ?? [],
        item.name ?? "",
        locale,
        "-"
    );

    const getSizeLabel = () => {
        if (hasOneSize(rawProductSizeLabel) || item.productKey === GIFT_WRAPPING_KEY) {
            return;
        }
        return productSizeLabel;
    };

    const getColorLabel = () => {
        const value = getLineItemAttribute(item, Attributes.dominantColor, locale);
        if (!value) {
            return;
        }
        return value;
    };

    const validOffers = offers.filter(
        (offer) => !isEmpty(offer.description) || !isEmpty(offer.disclaimer)
    );

    return (
        <ProductRowContainer sx={styles.container}>
            <ProductRowImage
                src={image?.url}
                alt={getImageAltTag(altTagPattern, image.label ?? "")}
            />
            <Box sx={styles.rightPanel}>
                <Box gap={"30%"}>
                    <ProductRowHeadline
                        textTransform={"capitalize"}
                        fontWeight="bold"
                        fontSize={["mobileBodyTextSmall", "desktopBodyTextSmall"]}
                    >
                        {item.name}
                    </ProductRowHeadline>
                </Box>
                {isDetailed && (
                    <ProductRowMeta
                        sx={styles.metaContainer}
                        colorProperty={{
                            value: getColorLabel(),
                            prefix: microcopies[CommonCMS.color],
                        }}
                        size={{ value: getSizeLabel(), prefix: microcopies[CommonCMS.size.title] }}
                        productNumber={isOrderTracking ? (item.productKey ?? undefined) : undefined}
                        quantity={isReturn ? item?.quantity * -1 : item?.quantity}
                    />
                )}
                {isDetailed && (
                    <Box gap={"30%"}>
                        <ProductRowPrice
                            sx={{
                                ...styles.price,
                                "& span": { color: isReturn ? "red.200" : undefined },
                            }}
                            priceNow={getPriceNow(item, locale)}
                            priceBefore={!isReturn ? getPriceBefore(item, locale) : undefined}
                        />
                    </Box>
                )}
            </Box>
            {validOffers.length > 0 && (
                <Flex sx={styles.offersWrapper} gap={1} pt={2} pb={2} direction="column">
                    {validOffers.map(({ description, disclaimer }, index) => {
                        const displayText = description || disclaimer;
                        return (
                            <Text color="black" fontWeight="normal" key={`${displayText}-${index}`}>
                                {displayText}
                            </Text>
                        );
                    })}
                </Flex>
            )}
        </ProductRowContainer>
    );
};

export default ProductRow;
